/**
 * Cost estimates from the tokens each call recorded and the prices in the model list
 * (obsessless-api src/evals/models.ts). Computed here rather than saved, so a price
 * change shows on every run. `null` means a model has no known price.
 *
 * The model under test and the judge are always kept apart: the model cost is what the
 * feature would cost in production, the judge cost is what grading it costs.
 */
import type { AiCallMeta, ArmSummary, EvalModel, EvalRunRecord, StoredTrialResult, TokenTotals } from '../types/evalTypes';

export const COST_NOTE = 'Estimate from list prices: Bedrock on-demand (global inference) for the model, Claude API for the judge.';

export interface CostSplit {
  /** The model under test. */
  model: number | null;
  /** The judge calls that graded it. */
  judge: number | null;
}

const pricingOf = (models: EvalModel[], modelId: string) => models.find((model) => model.id === modelId)?.pricing;

export function tokensCost(models: EvalModel[], modelId: string, tokens: TokenTotals): number | null {
  const pricing = pricingOf(models, modelId);
  if (!pricing) return null;
  return (
    (tokens.input * pricing.input +
      tokens.output * pricing.output +
      tokens.cacheCreation * pricing.cacheWrite +
      tokens.cacheRead * pricing.cacheRead) /
    1_000_000
  );
}

const callCost = (models: EvalModel[], meta: AiCallMeta): number | null =>
  tokensCost(models, meta.model, {
    input: meta.usage.inputTokens,
    output: meta.usage.outputTokens,
    cacheRead: meta.usage.cacheReadInputTokens,
    cacheCreation: meta.usage.cacheCreationInputTokens,
  });

/** `null` if any part is `null`. */
export const sumCosts = (costs: (number | null)[]): number | null =>
  costs.some((cost) => cost === null) ? null : costs.reduce<number>((total, cost) => total + (cost ?? 0), 0);

export const sumSplits = (splits: CostSplit[]): CostSplit => ({
  model: sumCosts(splits.map((split) => split.model)),
  judge: sumCosts(splits.map((split) => split.judge)),
});

export const trialCost = (models: EvalModel[], result: StoredTrialResult): CostSplit => ({
  model: result.output?.meta ? callCost(models, result.output.meta) : 0,
  judge: sumCosts(result.judgeCalls.map((call) => callCost(models, call))),
});

export function armCost(models: EvalModel[], run: Pick<EvalRunRecord, 'arms' | 'judgeModel'>, summary: ArmSummary): CostSplit {
  const model = run.arms.find((arm) => arm.id === summary.armId)?.model ?? '';
  return {
    model: tokensCost(models, model, summary.targetTokens),
    judge: tokensCost(models, run.judgeModel, summary.judgeTokens),
  };
}

export const CACHE_WRITE_NOTE =
  'The part of the model cost spent on prompt cache writes. A cache write costs 12.5 to 25 times a cache read, by model. Trials that start before the first write is done miss the cache and write it again, so this part changes from run to run. Hits: the share of cached tokens that were reads.';

/** The model cost of one arm spent on prompt cache writes. */
export function armCacheWriteCost(models: EvalModel[], run: Pick<EvalRunRecord, 'arms'>, summary: ArmSummary): number | null {
  const model = run.arms.find((arm) => arm.id === summary.armId)?.model ?? '';
  return tokensCost(models, model, { input: 0, output: 0, cacheRead: 0, cacheCreation: summary.targetTokens.cacheCreation });
}

export const WARM_CACHE_NOTE =
  'Model cost per 1,000 replies with cache writes priced as cache reads, as with steady production traffic. Parallel trials miss the prompt cache by chance, which makes the run cost noisy; this one is not. Use it to compare arms.';

/**
 * The model cost of one arm per 1,000 replies once the prompt cache is warm. `null` without
 * a price or a reply.
 */
export function armWarmCostPer1k(models: EvalModel[], run: Pick<EvalRunRecord, 'arms'>, summary: ArmSummary): number | null {
  const replies = summary.trials - summary.errors;
  const model = run.arms.find((arm) => arm.id === summary.armId)?.model ?? '';
  const { cacheRead, cacheCreation } = summary.targetTokens;
  const cost = tokensCost(models, model, { ...summary.targetTokens, cacheRead: cacheRead + cacheCreation, cacheCreation: 0 });
  return cost === null || replies === 0 ? null : (cost / replies) * 1000;
}

export const runCost =(models: EvalModel[], run: EvalRunRecord): CostSplit =>
  sumSplits(run.summary.arms.map((summary) => armCost(models, run, summary)));

export function formatUsd(cost: number | null): string {
  if (cost === null) return '–';
  if (cost === 0) return '$0';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  if (cost < 1) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(2)}`;
}
